import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PatronDashboard from "./PatronDashboard";
import { PageHeader, KpiCard, DataTable, StatusBadge } from "../components/Shell";
import { Button } from "../components/ui/button";
import {
  PencilSimpleLine,
  Plus,
  ReceiptX,
  ClockCounterClockwise,
  ShieldCheck,
  UsersThree,
  Warning,
  ArrowRight,
  FileX,
  CalendarX,
  Siren,
  CashRegister,
} from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  if (user?.role === "admin") {
    return <PatronDashboard />;
  }
  return <ManagerDashboard />;
}

function ManagerDashboard() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [incompleteTs, setIncomplete] = useState([]);
  const [dancers, setDancers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [todayCash, setTodayCash] = useState(null);
  const [monthlyCash, setMonthlyCash] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const now = new Date();
    const [s, reqs, ts, dd, tc, mc] = await Promise.all([
      api.get("/stats/dashboard"),
      api.get("/payment-requests"),
      api.get("/timesheets"),
      api.get("/dancers", { params: { statut: "active" } }),
      api.get(`/daily-cash/by-date/${today}`),
      api.get("/daily-cash/summary/monthly", { params: { year: now.getFullYear(), month: now.getMonth() + 1 } }),
    ]);
    setStats(s.data);
    setPending(reqs.data);
    setIncomplete(ts.data.filter((t) => t.incomplet));
    setDancers(dd.data);
    setTodayCash(tc.data);
    setMonthlyCash(mc.data);
    const bks = await Promise.all(
      dd.data.map((d) => api.get(`/dancers/${d.id}/bookings`).then((r) => r.data))
    );
    setBookings(bks.flat());
  };
  useEffect(() => {
    load();
  }, []);

  if (!stats) return <div className="text-[#a1a1aa]">Chargement...</div>;

  // Urgences
  const reqsEnAttente = pending.filter((r) => r.statut === "demande_envoyee");
  const reqsEnRetard = pending.filter((r) => r.en_retard);
  const today = new Date().toISOString().slice(0, 10);
  const bookingsACloturer = bookings.filter(
    (b) => b.statut === "actif" && b.date_fin_prevue && b.date_fin_prevue < today
  );
  const sansSiretOuRib = dancers.filter((d) => !d.siret || !d.titulaire_rib || !d.iban);
  const urgencesCount =
    reqsEnAttente.length + reqsEnRetard.length + incompleteTs.length + bookingsACloturer.length;

  const QuickAction = ({ to, icon: Icon, label, testId, tone = "primary" }) => (
    <button
      data-testid={testId}
      onClick={() => nav(to)}
      className={`group flex flex-col items-center justify-center gap-2 rounded-md border px-3 py-5 md:py-6 transition-all duration-200 ${
        tone === "primary"
          ? "bg-[#fbbf24] hover:bg-[#f59e0b] border-transparent text-black"
          : "bg-[#121217] hover:bg-[#1a1a21] border-[#27272a] text-white hover:border-[#52525b]"
      }`}
    >
      <Icon size={28} weight={tone === "primary" ? "fill" : "regular"} />
      <span className="text-sm md:text-base font-medium text-center leading-tight">{label}</span>
    </button>
  );

  return (
    <div>
      <PageHeader
        testId="dashboard-header"
        title="Dashboard"
        subtitle="Compte du jour, urgences et suivi opérationnel."
      />

      {/* 1. Saisir le compte du jour - BLOC PRIORITAIRE */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            data-testid="qa-daily-cash"
            onClick={() => nav("/daily-cash")}
            className="group rounded-md border p-5 md:p-6 text-left transition-all duration-200 hover:scale-[1.01] flex items-center gap-4"
            style={{ background: "#fbbf24", borderColor: "transparent" }}
          >
            <div
              className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center"
              style={{ background: "rgba(9,9,11,0.1)" }}
            >
              <CashRegister size={30} weight="fill" color="#09090b" />
            </div>
            <div className="min-w-0">
              <div className="label-xs" style={{ color: "rgba(9,9,11,0.7)" }}>
                Saisir / Modifier
              </div>
              <div className="font-display text-xl md:text-2xl font-medium text-black leading-tight">
                Compte du jour
              </div>
              <div className="text-xs mt-1" style={{ color: "rgba(9,9,11,0.7)" }}>
                CB + espèces − charges
              </div>
            </div>
          </button>

          <div className="surface p-5 md:p-6" data-testid="today-cash-summary">
            <div className="label-xs mb-1">Résumé du jour</div>
            <div className="text-xs text-[#71717a] mb-3">
              {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" })}
            </div>
            {todayCash ? (
              <>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <MiniLine label="CB" value={fmtMoney(todayCash.cb_total)} color="#60a5fa" />
                  <MiniLine label="Espèces" value={fmtMoney(todayCash.cash_total)} color="#34d399" />
                  <MiniLine label="CA" value={fmtMoney(todayCash.total_ca)} color="#fbbf24" />
                  <MiniLine label="Charges" value={fmtMoney(todayCash.charges_total)} color="#f87171" />
                </div>
                <div className="pt-3 border-t flex items-end justify-between" style={{ borderColor: "#27272a" }}>
                  <span className="label-xs">Net</span>
                  <span
                    className="font-display text-2xl md:text-3xl font-light"
                    style={{ color: todayCash.total_net >= 0 ? "#34d399" : "#f87171" }}
                  >
                    {fmtMoney(todayCash.total_net)}
                  </span>
                </div>
              </>
            ) : (
              <div className="text-sm text-[#71717a] py-6 text-center">
                Compte du jour pas encore saisi.
                <br />
                <button
                  data-testid="today-empty-cta"
                  onClick={() => nav("/daily-cash")}
                  className="mt-2 underline hover:text-white"
                >
                  Saisir maintenant
                </button>
              </div>
            )}
          </div>

          <div className="surface p-5 md:p-6" data-testid="monthly-cash-summary">
            <div className="label-xs mb-1">Résumé du mois</div>
            <div className="text-xs text-[#71717a] mb-3 capitalize">
              {new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
              {monthlyCash && <span> — {monthlyCash.days_saisis} j saisi{monthlyCash.days_saisis > 1 ? "s" : ""}</span>}
            </div>
            {monthlyCash && (
              <>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <MiniLine label="CB" value={fmtMoney(monthlyCash.total_cb)} color="#60a5fa" />
                  <MiniLine label="Espèces" value={fmtMoney(monthlyCash.total_cash)} color="#34d399" />
                  <MiniLine label="CA" value={fmtMoney(monthlyCash.total_ca)} color="#fbbf24" />
                  <MiniLine label="Charges" value={fmtMoney(monthlyCash.total_charges)} color="#f87171" />
                </div>
                <div className="pt-3 border-t flex items-end justify-between" style={{ borderColor: "#27272a" }}>
                  <span className="label-xs">Net du mois</span>
                  <span
                    className="font-display text-2xl md:text-3xl font-light"
                    style={{ color: monthlyCash.total_net >= 0 ? "#34d399" : "#f87171" }}
                  >
                    {fmtMoney(monthlyCash.total_net)}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* 2. Quick actions */}
      <div className="mb-8">
        <div className="label-xs mb-3">Actions rapides</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <QuickAction testId="qa-account" to="/accounts" icon={PencilSimpleLine} label="Saisir un compte danseuse" tone="primary" />
          <QuickAction testId="qa-dancer" to="/dancers" icon={Plus} label="Nouvelle danseuse" />
          <QuickAction testId="qa-requests" to="/payment-requests" icon={ReceiptX} label="Demandes de règlement" />
          <QuickAction testId="qa-timesheet" to="/timesheets" icon={ClockCounterClockwise} label="Nouveau pointage" />
          <QuickAction testId="qa-staff" to="/staff" icon={ShieldCheck} label="Personnel" />
        </div>
      </div>

      {/* Urgences block */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Siren size={16} color={urgencesCount > 0 ? "#f87171" : "#71717a"} weight={urgencesCount > 0 ? "fill" : "regular"} />
          <div className="label-xs" style={{ color: urgencesCount > 0 ? "#f87171" : undefined }}>
            Urgences / à traiter {urgencesCount > 0 && `(${urgencesCount})`}
          </div>
        </div>
        {urgencesCount === 0 ? (
          <div className="surface p-6 text-center text-[#71717a] text-sm">
            Tout est à jour. Aucune action urgente pour l'instant.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {reqsEnAttente.length > 0 && (
              <UrgenceCard
                testId="urgence-demandes"
                icon={ReceiptX}
                tone="#fbbf24"
                count={reqsEnAttente.length}
                label="Demandes à valider"
                hint={reqsEnAttente.slice(0, 2).map((r) => r.nom_scene).join(", ")}
                onClick={() => nav("/payment-requests?statut=demande_envoyee")}
              />
            )}
            {reqsEnRetard.length > 0 && (
              <UrgenceCard
                testId="urgence-retard"
                icon={Warning}
                tone="#ef4444"
                count={reqsEnRetard.length}
                label="Règlements en retard"
                hint="Validés mais non payés depuis > 48 h"
                onClick={() => nav("/payment-requests?filter=en_retard")}
              />
            )}
            {incompleteTs.length > 0 && (
              <UrgenceCard
                testId="urgence-pointages"
                icon={ClockCounterClockwise}
                tone="#f87171"
                count={incompleteTs.length}
                label="Pointages incomplets"
                hint="Heure d'arrivée sans heure de départ"
                onClick={() => nav("/timesheets?filter=incomplets")}
              />
            )}
            {bookingsACloturer.length > 0 && (
              <UrgenceCard
                testId="urgence-bookings"
                icon={CalendarX}
                tone="#60a5fa"
                count={bookingsACloturer.length}
                label="Bookings à clôturer"
                hint="Date de fin prévue dépassée"
                onClick={() => nav("/dancers?filter=booking_a_cloturer")}
              />
            )}
            {sansSiretOuRib.length > 0 && (
              <UrgenceCard
                testId="urgence-siret"
                icon={FileX}
                tone="#a1a1aa"
                count={sansSiretOuRib.length}
                label="Danseuses sans SIRET ou RIB"
                hint={sansSiretOuRib.slice(0, 2).map((d) => d.nom_scene).join(", ")}
                onClick={() => nav("/dancers?filter=sans_siret_rib")}
              />
            )}
          </div>
        )}
      </div>

      {/* Indicateurs principaux (4 seulement, priorité mobile) */}
      <div className="mb-8">
        <div className="label-xs mb-3">Indicateurs principaux</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard testId="kpi-bookings-actifs" label="Bookings actifs" value={stats.bookings_actifs} />
          <KpiCard testId="kpi-demandes" label="Demandes en attente" value={stats.demandes_en_attente} tone={stats.demandes_en_attente ? "jaunes" : "default"} />
          <KpiCard testId="kpi-reste" label="Reste à payer" value={fmtMoney(stats.reste_a_payer)} tone={stats.reste_a_payer > 0 ? "danger" : "default"} />
          <KpiCard testId="kpi-pointages-incomplets" label="Pointages incomplets" value={incompleteTs.length} tone={incompleteTs.length ? "danger" : "default"} />
        </div>
      </div>

      {/* Statistiques secondaires (repliable) */}
      <details className="mb-4">
        <summary className="label-xs cursor-pointer hover:text-white py-2 select-none">
          Statistiques détaillées (billets, jetons, finances, tops)
        </summary>
        <div className="pt-4 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Billets" value={stats.total_billets} tone="billets" hint="1€ × 45%" />
            <KpiCard label="Jetons jaunes" value={stats.total_jaunes} tone="jaunes" hint="20€ × 45%" />
            <KpiCard label="Jetons rouges" value={stats.total_rouges} tone="rouges" hint="10€" />
            <KpiCard label="Jetons bleus" value={stats.total_bleus} tone="bleus" hint="5€" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard label="Total brut généré" value={fmtMoney(stats.total_brut)} />
            <KpiCard label="Total dû danseuses" value={fmtMoney(stats.total_net)} tone="jaunes" />
            <KpiCard label="Total payé" value={fmtMoney(stats.total_paye)} tone="success" />
            <KpiCard label="Logement déduit" value={fmtMoney(stats.total_logement)} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KpiCard label="Acomptes" value={fmtMoney(stats.total_acomptes)} />
            <KpiCard label="Gain moyen / jour" value={fmtMoney(stats.gain_moyen_jour)} />
            <KpiCard label="Meilleure soirée" value={fmtMoney(stats.meilleure_soiree)} tone="success" />
          </div>
          <div>
            <div className="label-xs mb-3">Top danseuses par gains</div>
            <DataTable
              columns={[
                { title: "Scène", key: "nom_scene" },
                { title: "Gains", align: "right", render: (r) => fmtMoney(r.gains) },
                { title: "Billets", align: "right", key: "billets" },
                { title: "Jours", align: "right", key: "jours" },
              ]}
              rows={stats.top_gains || []}
              emptyText="Aucun compte enregistré"
            />
          </div>
        </div>
      </details>
    </div>
  );
}

function UrgenceCard({ testId, icon: Icon, tone, count, label, hint, onClick }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="surface p-4 text-left hover:bg-[#1a1a21] transition-all duration-200 flex items-start gap-3"
      style={{ borderLeft: `3px solid ${tone}` }}
    >
      <div className="mt-0.5">
        <Icon size={22} color={tone} weight="fill" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-2xl font-light" style={{ color: tone }}>{count}</span>
          <span className="text-sm font-medium">{label}</span>
        </div>
        {hint && <div className="text-xs text-[#71717a] mt-1 truncate">{hint}</div>}
      </div>
      <ArrowRight size={16} color="#71717a" className="mt-1 shrink-0" />
    </button>
  );
}


function MiniLine({ label, value, color }) {
  return (
    <div>
      <div className="label-xs" style={{ fontSize: 10 }}>{label}</div>
      <div className="font-mono text-sm mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
